//@ts-strict-ignore
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Header from './Header';
import Viewport from './Viewport';

const React = require('react');

const cellMetaDataShape = require('./PropTypeShapes/CellMetaDataShape');
require('styles/react-data-grid/react-data-grid-core.css');

const Grid: any = (props) => {
  const header = useRef<any>();
  const viewport = useRef<any>();
  const viewportContainer = useRef<any>();
  const _scrollLeft = useRef<any>();

  const getStyle = (): React.CSSProperties => {
    return {
      overflow: 'hidden',
      outline: 0,
      position: 'relative',
      minHeight: props.minHeight,
    };
  };

  const _onScroll = () => {
    if (_scrollLeft.current !== undefined) {
      header.current?.setScrollLeft(_scrollLeft.current);
      if (viewport.current) {
        viewport.current?.setScrollLeft(_scrollLeft.current);
      }
    }
  };

  const onScroll = (childProps) => {
    if (_scrollLeft.current !== childProps.scrollLeft) {
      _scrollLeft.current = childProps.scrollLeft;
      _onScroll();
    }
  };

  const onHeaderScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    if (_scrollLeft.current !== scrollLeft) {
      _scrollLeft.current = scrollLeft;
      header.current?.setScrollLeft(scrollLeft);
      const canvas = viewport.current?.canvas;
      canvas.scrollLeft = scrollLeft;
      viewport.current?.canvas.setScrollLeft(scrollLeft);
    }
  };

  useEffect(() => {
    _scrollLeft.current = viewport.current ? viewport.current?.getScroll().scrollLeft : 0;
    _onScroll();
    return () => {
      _scrollLeft.current = undefined;
    };
  }, []);

  const headerRows = props.headerRows || [];
  const EmptyRowsView = props.emptyRowsView;

  return (
    <div style={getStyle()} className="react-grid-Grid">
      <Header
        ref={header}
        columnMetrics={props.columnMetrics}
        onColumnResize={props.onColumnResize}
        height={props.rowHeight}
        totalWidth={props.totalWidth}
        headerRows={headerRows}
        sortColumn={props.sortColumn}
        sortDirection={props.sortDirection}
        draggableHeaderCell={props.draggableHeaderCell}
        onSort={props.onSort}
        onHeaderDrop={props.onHeaderDrop}
        onScroll={onHeaderScroll}
        getValidFilterValues={props.getValidFilterValues}
        cellMetaData={props.cellMetaData}
        includeHeaderRow={props.includeHeaderRow}
      />
      {props.rowsCount >= 1 || (props.rowsCount === 0 && !props.emptyRowsView) ? (
        <div
          ref={viewportContainer}
          onKeyDown={props.onViewportKeydown}
          onKeyUp={props.onViewportKeyup}
          onClick={props.onViewportClick}
          onDoubleClick={props.onViewportDoubleClick}
          onDragStart={props.onViewportDragStart}
          onDragEnd={props.onViewportDragEnd}
        >
          <Viewport
            ref={viewport}
            rowKey={props.rowKey}
            width={props.columnMetrics.width}
            rowHeight={props.rowHeight}
            rowRenderer={props.rowRenderer}
            rowGetter={props.rowGetter}
            rowsCount={props.rowsCount}
            selectedRows={props.selectedRows}
            expandedRows={props.expandedRows}
            columnMetrics={props.columnMetrics}
            totalWidth={props.totalWidth}
            onScroll={onScroll}
            onRows={props.onRows}
            cellMetaData={props.cellMetaData}
            rowOffsetHeight={props.rowOffsetHeight || props.rowHeight * headerRows.length}
            minHeight={props.minHeight}
            rowScrollTimeout={props.rowScrollTimeout}
            scrollToRowIndex={props.scrollToRowIndex}
            contextMenu={props.contextMenu}
            contextKey={props.contextKey}
            rowSelection={props.rowSelection}
            getSubRowDetails={props.getSubRowDetails}
            rowGroupRenderer={props.rowGroupRenderer}
            overScan={props.overScan}
            testId={props.testId}
            disableVirtualRows={props.disableVirtualRows}
          />
        </div>
      ) : (
        <div className="react-grid-Empty">
          <EmptyRowsView />
        </div>
      )}
    </div>
  );
};

Grid.displayName = 'Grid';

Grid.propTypes = {
  rowGetter: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  columns: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  columnMetrics: PropTypes.object,
  minHeight: PropTypes.number,
  totalWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  headerRows: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  rowHeight: PropTypes.number,
  rowRenderer: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.object]),
  emptyRowsView: PropTypes.func,
  expandedRows: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  selectedRows: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  rowSelection: PropTypes.oneOfType([
    PropTypes.shape({
      indexes: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
    PropTypes.shape({
      isSelectedKey: PropTypes.string.isRequired,
    }),
    PropTypes.shape({
      keys: PropTypes.shape({
        values: PropTypes.array.isRequired,
        rowKey: PropTypes.string.isRequired,
      }).isRequired,
    }),
    PropTypes.shape({
      all: PropTypes.bool,
    }),
  ]).isRequired,
  rowsCount: PropTypes.number,
  onRows: PropTypes.func,
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.oneOf(['ASC', 'DESC', 'NONE']),
  rowOffsetHeight: PropTypes.number.isRequired,
  onViewportKeydown: PropTypes.func.isRequired,
  onViewportKeyup: PropTypes.func,
  onViewportDragStart: PropTypes.func.isRequired,
  onViewportDragEnd: PropTypes.func.isRequired,
  onViewportClick: PropTypes.func.isRequired,
  onViewportDoubleClick: PropTypes.func.isRequired,
  onColumnResize: PropTypes.func,
  onSort: PropTypes.func,
  onHeaderDrop: PropTypes.func,
  cellMetaData: PropTypes.shape(cellMetaDataShape),
  rowKey: PropTypes.string.isRequired,
  rowScrollTimeout: PropTypes.number,
  scrollToRowIndex: PropTypes.number,
  contextMenu: PropTypes.element,
  getSubRowDetails: PropTypes.func,
  draggableHeaderCell: PropTypes.func,
  getValidFilterValues: PropTypes.func,
  rowGroupRenderer: PropTypes.func,
  overScan: PropTypes.object,
};

Grid.defaultProps = {
  rowHeight: 35,
  minHeight: 350,
};

export default Grid;
